import { Square } from 'lucide-react'
import QRCode from 'qrcode'
import { FC, useEffect, useState } from 'react'

import './PickPrint.css'
import printStyleContent from './PickPrint.css?inline'
import { useOutboundActions, useOutboundState } from './stores/outboundStore'

import PrintDialog from '@/components/common/PrintDialog'

// 打印内容组件
const PickPrint: FC = () => {
  // 出库状态
  const { printVisible, printingHouseBill } = useOutboundState()
  const { closePrintDialog } = useOutboundActions()

  const [qrCodeUrl, setQrCodeUrl] = useState<string>('')

  useEffect(() => {
    if (printingHouseBill?.houseBlNumber) {
      QRCode.toDataURL(printingHouseBill.houseBlNumber, {
        width: 100,
        margin: 1,
        errorCorrectionLevel: 'M'
      })
        .then((url) => {
          setQrCodeUrl(url)
        })
        .catch((err) => {
          console.error('生成二维码失败:', err)
        })
    }
  }, [printingHouseBill?.houseBlNumber])

  // 配送选项语义化
  const getDeliveryOptLabel = (value: string) => {
    const deliveryOptions = {
      CLEARANCE: '通関',
      WAREHOUSE: '倉庫',
      DIRECT: '西濃',
      CUSTOMER: 'お客様引き取り',
      SAGAWA: '佐川',
      YAMATO: 'ヤマト運輸'
    }
    return deliveryOptions[value as keyof typeof deliveryOptions] || value
  }

  // 使用旧数据结构生成表格行（后备方案）
  const generateAllRowsFromOldStructure = () => {
    if (!printingHouseBill?.cargoMarks || printingHouseBill.cargoMarks.length === 0) {
      return []
    }

    const allRows: React.ReactNode[] = []

    printingHouseBill.cargoMarks.forEach((cargo, cargoMarkIndex) => {
      allRows.push(
        <tr key={`cargo-${cargoMarkIndex}`}>
          <td>{cargoMarkIndex + 1}</td>
          <td>{cargo.cargoNumber || '-'}</td>
          <td>{cargo.packages || 0}</td>
          <td>-</td>
          <td>-</td>
          <td>-</td>
          <td>
            <div className="flex justify-center">
              <Square className="w-4 h-4" />
            </div>
          </td>
        </tr>
      )
    })

    return allRows
  }

  // 生成所有表格行数据
  const generateAllRows = () => {
    // 先检查是否有旧的数据结构作为后备
    const hasOldStructure = printingHouseBill?.cargoMarks && printingHouseBill.cargoMarks.length > 0
    const hasNewStructure = printingHouseBill?.warehousePallets && printingHouseBill.warehousePallets.length > 0

    // 如果新结构和旧结构都没有数据，返回空
    if (!hasOldStructure && !hasNewStructure) {
      return []
    }

    // 如果新结构没有数据但有旧结构，使用旧结构作为后备
    if (!hasNewStructure && hasOldStructure) {
      return generateAllRowsFromOldStructure()
    }
    const allRows: React.ReactNode[] = []

    // 按照货物标记和库位聚合托盘数据
    const cargoMarkGroups = new Map<string, {
      cargoNumber: string;
      totalPackages: number;
      positions: Map<string, {
        positionCode: string;
        palletCount: number;
        totalBoxes: number;
      }>;
    }>()

    // 遍历所有托盘，按照货物标记分组
    printingHouseBill.warehousePallets.forEach((pallet) => {
      const positionCode = pallet.warehousePosition?.code || '-'

      // 遍历托盘中的所有货物标记关联
      pallet.cargoMarkAssociations.forEach((association) => {
        const cargoMark = association.cargoMark
        if (!cargoMark) return

        const cargoKey = `${cargoMark.id}-${cargoMark.cargoNumber}`

        // 初始化货物标记组
        if (!cargoMarkGroups.has(cargoKey)) {
          cargoMarkGroups.set(cargoKey, {
            cargoNumber: cargoMark.cargoNumber,
            totalPackages: cargoMark.packages,
            positions: new Map()
          })
        }

        const cargoGroup = cargoMarkGroups.get(cargoKey)!

        // 初始化或更新位置信息
        if (!cargoGroup.positions.has(positionCode)) {
          cargoGroup.positions.set(positionCode, {
            positionCode,
            palletCount: 0,
            totalBoxes: 0
          })
        }

        const positionData = cargoGroup.positions.get(positionCode)!
        positionData.palletCount += 1
        positionData.totalBoxes += association.quantity
      })
    })

    // 根据 marksNumbers 的顺序排序货物标记组
    const sortedCargoGroups = Array.from(cargoMarkGroups.entries()).sort(([, cargoA], [, cargoB]) => {
      const marksNumbers = printingHouseBill?.marksNumbers || []
      const indexA = marksNumbers.indexOf(cargoA.cargoNumber)
      const indexB = marksNumbers.indexOf(cargoB.cargoNumber)

      // 如果都在 marksNumbers 中，按照 marksNumbers 的顺序排序
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB
      }

      // 如果只有一个在 marksNumbers 中，优先显示在 marksNumbers 中的
      if (indexA !== -1) return -1
      if (indexB !== -1) return 1

      // 如果都不在 marksNumbers 中，按照原有顺序（cargoNumber 字母顺序）
      return cargoA.cargoNumber.localeCompare(cargoB.cargoNumber)
    })

    // 生成表格行
    let rowIndex = 1
    sortedCargoGroups.forEach(([, cargoGroup]) => {
      const positions = Array.from(cargoGroup.positions.values())

      if (positions.length === 0) {
        // 如果没有位置信息，显示一行空数据
        allRows.push(
          <tr key={`cargo-${rowIndex}`}>
            <td>{rowIndex}</td>
            <td>{cargoGroup.cargoNumber || '-'}</td>
            <td>{cargoGroup.totalPackages || 0}</td>
            <td colSpan={4}>-</td>
          </tr>
        )
        rowIndex++
        return
      }

      positions.forEach((position, positionIndex) => {
        allRows.push(
          <tr key={`cargo-${rowIndex}-position-${positionIndex}`}>
            {positionIndex === 0 ? (
              <>
                <td rowSpan={positions.length}>{rowIndex}</td>
                <td rowSpan={positions.length}>{cargoGroup.cargoNumber || '-'}</td>
                <td rowSpan={positions.length}>{cargoGroup.totalPackages || 0}</td>
              </>
            ) : null}
            <td>
              <strong>{position.positionCode}</strong>
            </td>
            <td>{position.palletCount}</td>
            <td>{position.totalBoxes}</td>
            <td>
              <div className="flex justify-center">
                <Square className="w-4 h-4" />
              </div>
            </td>
          </tr>
        )
      })
      rowIndex++
    })

    return allRows
  }

  // 生成合计行
  const generateTotalRow = () => {
    let totalPallets = 0
    let totalBoxes = 0

    // 检查数据结构
    const hasNewStructure = printingHouseBill?.warehousePallets && printingHouseBill.warehousePallets.length > 0
    const hasOldStructure = printingHouseBill?.cargoMarks && printingHouseBill.cargoMarks.length > 0

    if (hasNewStructure) {
      // 使用新数据结构计算
      printingHouseBill.warehousePallets.forEach((pallet) => {
        totalPallets += 1
        pallet.cargoMarkAssociations.forEach((association) => {
          totalBoxes += association.quantity
        })
      })
    } else if (hasOldStructure) {
      // 使用旧数据结构计算 - 累计所有货物标记的packages
      totalBoxes = printingHouseBill.cargoMarks?.reduce((sum, cargo) => sum + (cargo.packages || 0), 0) || 0
      // 对于旧结构，我们没有准确的托盘数，使用货物标记数作为估算
      totalPallets = printingHouseBill.cargoMarks?.length || 0
    } else {
      // 没有数据时，尝试使用总的packages
      totalBoxes = printingHouseBill?.packages || 0
    }

    return (
      <tr>
        <td>運送</td>
        <td colSpan={2}>
          {printingHouseBill?.deliveryOpt
            ? getDeliveryOptLabel(printingHouseBill.deliveryOpt)
            : ''}
        </td>
        <td style={{ fontSize: '12px' }}>合計</td>
        <td>{totalPallets}</td>
        <td>{totalBoxes}</td>
        <td>-</td>
      </tr>
    )
  }

  const allRows = generateAllRows()

  return (
    <PrintDialog
      pageWidth={100}
      visible={printVisible}
      pageCssContent={printStyleContent}
      onCancel={() => closePrintDialog()}
    >
      <div className="continuous-print">
        <h1 style={{ letterSpacing: '0.5em' }}>出庫指示書</h1>
        <table style={{ border: '2px solid #000' }}>
          <thead>
            <tr>
              <th>種類</th>
              <th colSpan={3}>一般搬出</th>
              {/* 検査搬出 */}
              <th colSpan={3} rowSpan={4}>
                {qrCodeUrl && (
                  <img
                    className="print-qr-label"
                    style={{ margin: '0 auto' }}
                    src={qrCodeUrl}
                    alt="出库二维码"
                  />
                )}
              </th>
            </tr>
            <tr>
              <th>日付</th>
              <th colSpan={3}>{new Date().toISOString().split('T')[0]}</th>
            </tr>
            <tr>
              <th>NO.</th>
              <th colSpan={3}>
                {printingHouseBill?.masterBill.customerNumber}
              </th>
            </tr>
            <tr>
              <th>HBL</th>
              <th colSpan={3}>{printingHouseBill?.houseBlNumber}</th>
            </tr>
            <tr
              style={{
                borderTop: '2px solid #000'
              }}
            >
              <th style={{ width: '10%' }}>番号</th>
              <th style={{ width: 'auto' }}>MARK</th>
              <th style={{ width: '12%' }}>総数</th>
              <th style={{ width: '10%' }}>位置</th>
              <th style={{ width: '16%', fontSize: '12px' }}>パレット</th>
              <th style={{ width: '12%' }}>箱数</th>
              <th style={{ width: '10%' }}>済み</th>
            </tr>
          </thead>

          <tbody>
            {allRows}
            {generateTotalRow()}
          </tbody>
          <tfoot>
            <tr>
              <td
                colSpan={7}
                style={{
                  textAlign: 'left',
                  paddingBottom: '2em',
                  lineHeight: '1.5em'
                }}
              >
                {printingHouseBill?.deliveryNote}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </PrintDialog>
  )
}

export default PickPrint
